import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {   RiArrowRightLine } from 'react-icons/ri';

const HeroSection = ({ scale, opacity, slides = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    // Orchestrate slideshow cycle every 6 seconds
    useEffect(() => {
        if (slides.length === 0) return;
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % slides.length);
        }, 6000);
        return () => clearInterval(timer);
    }, [slides]);

    const currentSlide = slides[currentIndex] || {};

    return (
        <section className="relative isolate min-h-screen flex flex-col justify-center overflow-hidden bg-black">

            {/* Cinematic Slideshow Background */}
            <div className="absolute inset-0 -z-30 overflow-hidden">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, scale: 1 }}
                        animate={{ opacity: 0.6, scale: 1.15 }}
                        exit={{ opacity: 0, scale: 1.2 }}
                        transition={{ duration: 6, ease: "linear" }}
                        className="absolute inset-0"
                    >
                        <img
                            src={currentSlide.image}
                            alt={`Environmental Scene ${currentIndex + 1}`}
                            className="w-full h-full object-cover select-none pointer-events-none grayscale-[0.2]"
                        />
                    </motion.div>
                </AnimatePresence>

                {/* Persistent Readability Layering - Softened for better transparency */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80 backdrop-blur-[0.5px]" />
                <div className="absolute inset-0 shadow-[inset_0_0_150px_rgba(0,0,0,0.6)]" />
            </div>

            {/* Floating Luminous Mesh */}
            <div
                className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80 pointer-events-none opacity-30"
                aria-hidden="true"
            >
                <motion.div
                    animate={{
                        rotate: [0, 5, 0],
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3]
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-emerald-600 to-transparent sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                    style={{
                        clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
                    }}
                />
            </div>

            <motion.div
                style={{ scale, opacity }}
                className="relative mx-auto max-w-7xl px-6 lg:px-8 z-10 flex flex-col items-center justify-center flex-1 text-center"
            >
                <br /><br />

                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentIndex}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="flex flex-col items-center"
                    >
                        <motion.h1
                            className="text-3xl sm:text-7xl lg:text-8xl font-black tracking-tight text-white leading-[1.2] sm:leading-[0.9] drop-shadow-2xl font-display"
                        >
                            {currentSlide.title} <br />
                            <span className="text-emerald-500 italic font-medium drop-shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                                {currentSlide.highlight}
                            </span>
                        </motion.h1>

                        <motion.p
                            className="mt-6 text-base sm:text-xl leading-relaxed text-slate-300 font-sans font-medium max-w-2xl mx-auto"
                        >
                            {currentSlide.description}
                        </motion.p>
                    </motion.div>
                </AnimatePresence>

                {/* Main Actions */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8"
                >
                    <button className="w-full sm:w-auto flex items-center justify-center gap-3 px-10 py-4 bg-white text-black font-black text-xs uppercase tracking-[0.2em] rounded-full hover:bg-emerald-400 transition-all duration-500 shadow-2xl group">
                        Get Started
                        <RiArrowRightLine className="group-hover:translate-x-1 transition-transform" />
                    </button>

                    <a href="#" className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 hover:text-white transition-colors group">
                        Learn more
                        <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                    </a>
                </motion.div>
            </motion.div>

            {/* Absolute Bottom Slideshow Indicators */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className={`h-1 transition-all duration-700 rounded-full ${i === currentIndex ? 'w-10 bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'w-4 bg-white/20 hover:bg-white/40'}`}
                    />
                ))}
            </div>

            {/* Decorative Bottom Mesh */}
            <div
                className="absolute inset-x-0 bottom-0 -z-10 transform-gpu overflow-hidden blur-3xl pointer-events-none opacity-20"
                aria-hidden="true"
            >
                <div
                    className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-emerald-900 to-black opacity-60 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
                    style={{
                        clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)'
                    }}
                />
            </div>

        </section>
    );
};

export default HeroSection;
